import { useCallback, useRef } from 'react';
import { API_CALL_FAILED } from '@/consts/events';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useRegistration } from '@/context/RegistrationContext';
import { apiClient, DEFAULT_CUFF_SIZE } from '@/services/api';
import type { SolutionType, UserData } from '@/types/registration';
import { signupErrStatus } from '@/components/webreg/useWregSignup';
import type { ShippingForm } from '@/components/webreg/screens/wregShippingFields';
import { WREG_STEP_NAME } from '@/components/webreg/wregSteps';

const DEFAULT_SOLUTIONS: SolutionType[] = ['BP'];

export const wregShippingIdentity = (f: ShippingForm): string =>
  [f.first, f.last, f.street, f.apt, f.city, f.state, f.zip].map((v) => v.trim()).join('|');

export type WregShippingOutcome = { ok: true; sent: boolean } | { ok: false };

export const useWregShipping = () => {
  const { trackError } = useAnalytics();
  const { registrationData } = useRegistration();
  const sent = useRef<string | null>(null);
  const { userData, account } = registrationData;

  const submit = useCallback(
    async (address: ShippingForm, override?: UserData): Promise<WregShippingOutcome> => {
      const user = override ?? userData;
      if (!user?.authToken) return { ok: true, sent: false };

      const identity = wregShippingIdentity(address);
      if (sent.current === identity) return { ok: true, sent: false };

      try {
        await apiClient.shippingDetails(
          {
            firstName: address.first.trim(),
            lastName: address.last.trim(),
            streetaddress: [address.street.trim(), address.apt.trim()]
              .filter(Boolean)
              .join(' '),
            city: address.city.trim(),
            state: address.state.trim(),
            zipcode: address.zip,
            phonenumber: user.phonenumber || account.phoneNumber,
            cuffSize: DEFAULT_CUFF_SIZE,
            solutions: user.solutions?.length
              ? user.solutions.map((s) => s.solutionType)
              : DEFAULT_SOLUTIONS,
          },
          user,
        );
        sent.current = identity;
        return { ok: true, sent: true };
      } catch (err) {
        trackError({
          event: API_CALL_FAILED,
          properties: {
            service: 'shipping',
            status: signupErrStatus(err),
            step_name: WREG_STEP_NAME.monitor,
          },
        });
        return { ok: false };
      }
    },
    [account.phoneNumber, trackError, userData],
  );

  return { submit };
};
